import { useCallback, useMemo, useRef } from 'react';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

import useLocalStorage from '@webclient/hooks/useLocalStorage';
import { useGetInfiniteNotificationsLazily } from '@webclient/hooks/useNotificationsQuery';

import { Text } from '@knowz/knowz-ui';
import { IconButton } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import NotificationItem from '@webclient/layouts/main/sections/header/sections/notifications/notifications-list/notification-item';
import {
  LoaderContainer,
  StyledNotificationsStack,
  StyledPaper,
  StyledTitleStack,
} from '@webclient/layouts/main/sections/header/sections/notifications/notifications-list/styles';

export default function NotificationsList() {
  const { t } = useTranslate();
  const {
    value: notificationsPendingFilter,
    setValue: setNotificationsPendingFilter,
  } = useLocalStorage<'on' | 'off'>('notificationsPendingFilter', 'off');

  const {
    data,
    isLoading,
    hasNextPage,
    isError,
    error,
    fetchNextPage,
    isFetching,
  } = useGetInfiniteNotificationsLazily();

  const observer = useRef<IntersectionObserver>();
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        entries[0].isIntersecting &&
          hasNextPage &&
          !isFetching &&
          fetchNextPage();
      });

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetching, isLoading],
  );

  const notifications = useMemo(() => {
    const notifications = data?.pages?.length
      ? data.pages.flatMap((page) => page.data || [])
      : [];

    if (notificationsPendingFilter === 'on') {
      return notifications.filter((notification) => !notification.isDismissed);
    }

    return notifications;
  }, [data?.pages, notificationsPendingFilter]);

  if (isLoading) {
    return (
      <LoaderContainer>
        <CircularProgress />
      </LoaderContainer>
    );
  }

  if (isError) {
    console.error(error);
    return (
      <Alert severity="error">
        {t(
          'layout.main.sections.header.notifications.notificationsList.error',
          'Failed to load notifications',
        )}
      </Alert>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <Alert severity="info">
        {t(
          'layout.main.sections.header.notifications.notificationsList.empty',
          'No notifications',
        )}
      </Alert>
    );
  }

  return (
    <>
      <StyledTitleStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text
          variant="textLg"
          fontWeight="semibold"
        >
          {t(
            'layout.main.sections.header.notifications.notificationsList.title',
            'Notifications',
          )}
        </Text>

        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => {
            setNotificationsPendingFilter(
              notificationsPendingFilter === 'on' ? 'off' : 'on',
            );
          }}
        >
          <Tooltip
            title={
              notificationsPendingFilter === 'on'
                ? t(
                    'layout.main.sections.header.notifications.notificationsList.filterOn',
                    'Hiding dismissed notifications',
                  )
                : t(
                    'layout.main.sections.header.notifications.notificationsList.filterOff',
                    'Showing all notifications',
                  )
            }
            placement="right"
            arrow
          >
            <IconButton
              size="small"
              variant="ghost"
              color={
                notificationsPendingFilter === 'on' ? 'primary' : 'secondary'
              }
              onClick={() => {
                setNotificationsPendingFilter(
                  notificationsPendingFilter === 'on' ? 'off' : 'on',
                );
              }}
              sx={{
                height: '24px !important',
                width: '24px !important',

                '& svg': {
                  width: '18px !important',
                  height: '18px !important',
                },
              }}
            >
              {notificationsPendingFilter === 'on' ? (
                <FilterAltIcon />
              ) : (
                <FilterAltOffIcon />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </StyledTitleStack>

      <StyledNotificationsStack gap={6}>
        {notifications.map((notification, index) => {
          const itemProps =
            index === notifications.length - 1 ? { ref: lastElementRef } : {};

          return (
            <StyledPaper
              sx={{ opacity: notification.isDismissed ? 0.7 : 1 }}
              key={notification.id}
              {...itemProps}
            >
              <NotificationItem notification={notification} />
            </StyledPaper>
          );
        })}

        {hasNextPage && isFetching && (
          <Stack sx={{ alignItems: 'center' }}>
            <CircularProgress />
          </Stack>
        )}
      </StyledNotificationsStack>
    </>
  );
}
